import React, {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Outlet, useParams } from "react-router-dom";
import { getMyPolicies, PolicySummary, PolicyTermSummary } from "../../api/services";
import { buildTestPolicy, isRenewalTestMode } from "./renewalTestMode";

export type RenewalIntent = "renew" | "move-out" | "not-sure";

/** Requested term length on the "I'm Not Sure Yet" preference step. */
export type RenewalPreference =
  | "12-month"
  | "6-month"
  | "month-to-month"
  | "undecided";

export interface ContactDraft {
  name: string;
  email: string;
  phone: string;
  phoneVerified: boolean;
}

export interface NotesDraft {
  /** Yes/No — "anything else to share?". null until answered. */
  hasNotes: boolean | null;
  text: string;
}

export interface ConditionDraft {
  /** Yes/No — "any damages or maintenance to report?". */
  hasDamage: boolean | null;
  description: string;
  /** File names only for now; upload wiring comes with the backend. */
  files: File[];
}

export interface RenewalDraft {
  intent: RenewalIntent | null;
  preference: RenewalPreference | null;
  contact: ContactDraft;
  notes: NotesDraft;
  moveOutDate: string;
  condition: ConditionDraft;
  paymentMethodId: number | null;
}

interface RenewalContextValue {
  /** `policy.nextTerm.uuid` from the route. */
  termUuid: string;
  policy: PolicySummary | null;
  nextTerm: PolicyTermSummary | null;
  loading: boolean;
  /** Set when the term uuid matches no policy the tenant owns. */
  notFound: boolean;
  error: string | null;

  draft: RenewalDraft;
  update: (patch: Partial<RenewalDraft>) => void;
  reset: () => void;
}

const emptyDraft = (): RenewalDraft => ({
  intent: null,
  preference: null,
  contact: { name: "", email: "", phone: "", phoneVerified: false },
  notes: { hasNotes: null, text: "" },
  moveOutDate: "",
  condition: { hasDamage: null, description: "", files: [] },
  paymentMethodId: null,
});

const RenewalContext = createContext<RenewalContextValue | undefined>(undefined);

/**
 * Resolves the policy for a renewal-concierge visit and holds the in-progress
 * answers for every step under `/renewal/:termUuid/*`.
 *
 * The route is keyed on `nextTerm.uuid` (that's what the outreach email will
 * link to), but there's no endpoint to fetch a policy by term uuid — so we
 * pull the tenant's policies and match locally. That also scopes the flow to
 * policies the logged-in tenant actually owns: an unknown uuid is a 404 here
 * rather than someone else's renewal.
 *
 * Nothing is persisted yet — `draft` lives in memory for the click-through.
 */
export const RenewalProvider: React.FC<{ children?: ReactNode }> = ({
  children,
}) => {
  const { termUuid } = useParams<{ termUuid: string }>();
  const [policy, setPolicy] = useState<PolicySummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [draft, setDraft] = useState<RenewalDraft>(emptyDraft);

  useEffect(() => {
    if (!termUuid) return;
    let cancelled = false;
    setLoading(true);
    setNotFound(false);
    setError(null);

    // `/renewal/test` loads a fixture so the flow is clickable without a real
    // renewal term. Short-circuits before any network call.
    if (isRenewalTestMode(termUuid)) {
      setPolicy(buildTestPolicy());
      setLoading(false);
      return;
    }

    getMyPolicies()
      .then((res) => {
        if (cancelled) return;
        const match =
          res.data.find((p) => p.nextTerm?.uuid === termUuid) ?? null;
        if (!match) {
          setNotFound(true);
          return;
        }
        setPolicy(match);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error("Failed to load renewal policy:", err);
        setError("We couldn't load your renewal. Please try again.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [termUuid]);

  const value = useMemo<RenewalContextValue>(
    () => ({
      termUuid: termUuid ?? "",
      policy,
      nextTerm: policy?.nextTerm ?? null,
      loading,
      notFound,
      error,
      draft,
      update: (patch) => setDraft((prev) => ({ ...prev, ...patch })),
      reset: () => setDraft(emptyDraft()),
    }),
    [termUuid, policy, loading, notFound, error, draft],
  );

  return (
    <RenewalContext.Provider value={value}>
      {children ?? <Outlet />}
    </RenewalContext.Provider>
  );
};

export const useRenewal = (): RenewalContextValue => {
  const ctx = useContext(RenewalContext);
  if (!ctx) {
    throw new Error("useRenewal must be used within a RenewalProvider");
  }
  return ctx;
};
